import { useMemo, useRef, useState } from "react";
import { bool, func, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { setEstimationStatusQuery } from "@query";
import { getSectionWtClass } from "@utils";
import { useCheckEstimateEditorAccess, useEditorWebsocket } from "@hooks";
import {
  useEstimateEditorStore,
  useEstimateStructure,
  useEstimateTableSettings, 
  useEstimateEditorSettings,
  WorkTypeManager,
  UserPermits, 
} from "@tools";
import { Alert, MenuButton, PopMenu } from "@components";
import { IconButton } from "@material-ui/core";
import { Settings, Trash, Percent, Users } from "@assets";
import { Cached, Clear, Check, VisibilityOff } from "@material-ui/icons";
import classnames from "classnames";

export const HeaderWorkTypeActions = observer(({
  workType,
  section,
  hasPermit,
  isPending,
  userSeesOnlyPermittedWorkTypes,
  openWorkTypeAutomationDialog,
  openWorkTypeRoundingsDialog,
}) => {
  const { isLibrary } = useEstimateEditorSettings();
  const { visibleWorkTypes } = useEstimateTableSettings();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();
  const socket = useEditorWebsocket();
  const { estimateUuid } = useParams();
  const { t } = useTranslation();

  const [ visible, show ] = useState(false);
  const [ alertVisible, showAlert ] = useState(false);
  const [ workTypeModal, openWorkTypeModal ] = useState(false);
  const [ userPermitsVisible, openUserPermits ] = useState(false);

  const anchor = useRef(null);
  
  const structureWorkType = useMemo(() => (
    structure.getWorkTypeById(workType.id)
  ), [workType.id]);

  const { isOwner } = useCheckEstimateEditorAccess(editorStore.estimateUsers);

  const handleWorkTypeRemove = () => {
    if (isLibrary) {
      // structureWorkType.setBreakdownVisibility(!structureWorkType?.inBreakdown);
      structure.setWorkTypeVisibility?.(workType.id, !workType.invisible, true);
      return;
    }
    showAlert(true);
  };

  const removeWorkType = async () => {
    structure.historyManager.startGroup();
    section.removeWorkType(workType.id, true);
    
    const path = structureWorkType.treePath.join("/");
    const removed = structure.checkAndRemoveSectionWorkType(workType.id);
    
    if(removed && path && !isLibrary) {
      socket?.requestCommentPathRemove(path);
      socket?.requestCommentPathRemove("t"+path);
    }
    structure.historyManager.stopGroup();
  };

  const handleWorkTypePermits = () => {
    openUserPermits(true);
    handleMenuClose();
  };

  const handleStatusChange = async () => {
    const status = editorStore.updateLocalPermit(workType.id)
    socket?.setPermitStatus(workType.id, status)
    await setEstimationStatusQuery(estimateUuid, workType.id);
  };

  const toggleWorkTypeAutomationDialog = () => {
    openWorkTypeAutomationDialog(true);
    handleMenuClose();
  };

  const toggleWorkTypeRoundingsDialog = () => {
    openWorkTypeRoundingsDialog(true);
    handleMenuClose();
  };

  const handleWorkTypeChange = () => {
    openWorkTypeModal(true);
    handleMenuClose();
  };

  const handleMenuClose = () => {
    show(false);
  };

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        onClick={() => show(!visible)}
        ref={anchor}
        tabIndex={-1}
        aria-label={`"${name}" work type settings`}
        className={classnames("noDrag cellHoverIcon absolute text-sm", getSectionWtClass(section.id, workType.id))}
        style={{ right: -16 }}
      >
        <Settings />
      </IconButton>

      <PopMenu
        show={visible}
        anchor={anchor?.current}
        onClickAway={() => show(false)}
        style={{ minHeight: 0, width: 208 }}
        showArrow
      >
        {
          hasPermit &&
          <MenuButton
            icon={isPending ? <Check /> : <Clear />}
            onClick={handleStatusChange}
          >
            {t("views.editor.status_change")}
          </MenuButton>
        }
        {
          !isLibrary &&
          <MenuButton icon={<Percent />} onClick={toggleWorkTypeAutomationDialog}>
            {t("views.editor.dialogs.automation.title")}
          </MenuButton>
        }
        {
          !isLibrary &&
          <MenuButton icon={<Cached />} onClick={toggleWorkTypeRoundingsDialog}>
            {t("views.editor.dialogs.roundings.title")}
          </MenuButton>
        }
        {
          isOwner &&
          <MenuButton icon={<Users />} onClick={handleWorkTypePermits}>
            {t("views.editor.workType_permits")}
          </MenuButton>
        }
        {
          !userSeesOnlyPermittedWorkTypes &&
          <MenuButton icon={<Settings />} onClick={handleWorkTypeChange}>
            {t("views.editor.change_workType")}
          </MenuButton>
        }
        <MenuButton
          icon={isLibrary ? <VisibilityOff /> : <Trash color="error" />}
          onClick={handleWorkTypeRemove}
        >
          {t(
            `views.editor.${
              isLibrary ? "hide" : "remove"
            }_workType`
          )}
        </MenuButton>
      </PopMenu>
      <Alert
        isOpen={alertVisible}
        title={t(
          `alerts.editor.${
            structure.sectionsWithWorkType(workType.id)
              ? "tag_remove_section"
              : "tag_remove"
          }`
        )}
        onAccept={removeWorkType}
        onCancel={() => showAlert(false)}
        acceptText={t("common.remove")}
        cancelText={t("common.cancel")}
      />
      {workTypeModal && (
        <WorkTypeManager
          open={workTypeModal}
          section={section}
          sectionWorkTypeId={workType.id}
          onClose={() => openWorkTypeModal(false)}
          anchorEl={anchor?.current}
          isLibrary={isLibrary}
          visibleWorkTypes={visibleWorkTypes}
        />
      )}
      {isOwner && userPermitsVisible && (
        <UserPermits
          isOpen={true}
          onClose={() => openUserPermits(false)}
          workTypeId={workType.id}
          workTypeName={workType.name}
        />
      )}
    </>
  );
});

HeaderWorkTypeActions.propTypes = {
  workType: object.isRequired,
  section: object.isRequired,
  openWorkTypeAutomationDialog: func.isRequired,
  openWorkTypeRoundingsDialog: func.isRequired,
  hasPermit: bool,
  isPending: bool,
  userSeesOnlyPermittedWorkTypes: bool,
};
